import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import DirectionsWalkIcon from "../components/icons/DirectionsWalkIcon";
import SixMinuteWalkRouteMap from "../components/charts/SixMinuteWalkRouteMap";
import ResultCard from "../components/common/ResultCard";
import { GOOGLE_MAPS_ENABLED } from "../config/googleMaps";
import RemotePodLoader from "../components/recording/RemotePodLoader";
import { trackEvent } from "../utils/analytics/track";
import { RECORDING_MODES } from "../utils/recordingModes";
import { buildSixMinuteWalkSessionStorageKey } from "../utils/recording/sixMinuteWalkStorage";

const loadRecordingTidalBreathing = () => import("recordingTidalBreathing/App");

const BASE_PATH = "/recording/six-minute-walk";
const PRE_WALK_RECORDING_DURATION_SECONDS = 45;
const POST_WALK_RECORDING_DURATION_SECONDS = 60;
const WALK_DURATION_SECONDS = 6 * 60;
const WALK_TURN_AROUND_SECONDS = 3 * 60;
const POST_RECORDING_RESUME_WINDOW_MS = 60 * 1000;
const MAX_ACCEPTED_ACCURACY_METERS = 60;
const MAX_ACCEPTED_SPEED_METRES_PER_SECOND = 3;
const RETURN_TO_START_THRESHOLD_METERS = 25;
const ROUTE_TRACE_MIN_POINT_DISTANCE_METERS = 2;
const ROUTE_TRACE_MIN_POINT_INTERVAL_MS = 2000;
const ROUTE_TRACE_MAX_POINTS = 180;
const WALK_DEBUG_ENABLED = process.env.NODE_ENV === "development";

const PRE_WALK_PHASE_SCHEDULE = [
  { seconds: 20, key: "cameraPage.phases.normalPre" },
  { seconds: 3, key: "cameraPage.phases.fastInhale" },
  { seconds: 3, key: "cameraPage.phases.fastExhaleBlast" },
  { seconds: 19, key: "cameraPage.phases.resume" },
];

const POST_WALK_PHASE_SCHEDULE = [
  { seconds: 10, key: "cameraPage.phases.normalPre" },
  { seconds: 3, key: "cameraPage.phases.fastInhale" },
  { seconds: 3, key: "cameraPage.phases.fastExhaleBlast" },
  { seconds: 44, key: "cameraPage.phases.resume" },
];

function logSixMinuteWalkFlowDebug(level, message, details = undefined) {
  if (!WALK_DEBUG_ENABLED || typeof console === "undefined") {
    return;
  }

  const logger =
    level === "error"
      ? console.error
      : level === "warn"
        ? console.warn
        : console.info;

  if (details === undefined) {
    logger(`[SixMinuteWalkFlow] ${message}`);
    return;
  }

  logger(`[SixMinuteWalkFlow] ${message}`, details);
}

function getStatusValue(event) {
  if (typeof event === "string") return event;
  if (event && typeof event === "object" && typeof event.status === "string") {
    return event.status;
  }
  return null;
}

function getUploadId(event) {
  if (!event || typeof event !== "object") return null;

  const candidate =
    event.uploadId ?? event.recordingId ?? event.docid ?? event.docId ?? null;

  if (candidate == null) return null;
  const normalized = String(candidate).trim();
  return normalized || null;
}

function createAssessmentId() {
  const iso = new Date().toISOString().replace(/:/g, "-");
  const rand = Math.random().toString(36).slice(2, 8);

  return `6mwt-${iso}-${rand}`;
}

function createAssessmentState(overrides = {}) {
  return {
    assessmentId: createAssessmentId(),
    preMeasurementId: null,
    walk: null,
    postResume: null,
    returnedAt: null,
    postRecordingStartedAt: null,
    recoveryDelayMs: null,
    postMeasurementId: null,
    ...overrides,
  };
}

function readStoredAssessment(uid) {
  if (!uid || typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(
      buildSixMinuteWalkSessionStorageKey(uid),
    );
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredAssessment(uid, value) {
  if (!uid || typeof window === "undefined") {
    return;
  }

  try {
    if (!value) {
      window.sessionStorage.removeItem(
        buildSixMinuteWalkSessionStorageKey(uid),
      );
      return;
    }

    window.sessionStorage.setItem(
      buildSixMinuteWalkSessionStorageKey(uid),
      JSON.stringify(value),
    );
  } catch {}
}

function parseTimestampMs(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const timestampMs = Date.parse(value);
  return Number.isFinite(timestampMs) ? timestampMs : null;
}

function buildPostRecordingResumeState({
  completedAt,
  trackingStoppedAt,
  returnedAt,
  continuedAt = null,
}) {
  const resumeStartedAtMs =
    parseTimestampMs(trackingStoppedAt) ?? parseTimestampMs(completedAt);

  if (!Number.isFinite(resumeStartedAtMs)) {
    return null;
  }

  return {
    startedAt: new Date(resumeStartedAtMs).toISOString(),
    expiresAt: new Date(
      resumeStartedAtMs + POST_RECORDING_RESUME_WINDOW_MS,
    ).toISOString(),
    timeToRecordingStartedAt:
      returnedAt || trackingStoppedAt || completedAt || null,
    continuedAt: continuedAt || null,
  };
}

function hasPostRecordingContinueIntent(assessment) {
  return Boolean(
    assessment?.postRecordingStartedAt || assessment?.postResume?.continuedAt,
  );
}

function hasActivePostRecordingResume(assessment, nowMs = Date.now()) {
  if (
    !assessment?.walk ||
    !assessment?.preMeasurementId ||
    hasPostRecordingContinueIntent(assessment) ||
    assessment?.postMeasurementId
  ) {
    return false;
  }

  const expiresAtMs = parseTimestampMs(assessment?.postResume?.expiresAt);
  return Number.isFinite(expiresAtMs) && nowMs <= expiresAtMs;
}

function getPostRecordingResumeStartAt(assessment) {
  return (
    assessment?.postResume?.timeToRecordingStartedAt ||
    assessment?.returnedAt ||
    assessment?.walk?.returnedAt ||
    assessment?.walk?.trackingStoppedAt ||
    assessment?.walk?.completedAt ||
    null
  );
}

function formatDurationLabel(totalSeconds) {
  const safeSeconds = Math.max(0, Math.round(totalSeconds));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function formatDistanceLabel(distanceMeters) {
  if (typeof distanceMeters !== "number" || !Number.isFinite(distanceMeters)) {
    return "0";
  }

  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: distanceMeters >= 1000 ? 0 : 1,
  }).format(distanceMeters);
}

function readFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function computeWalkProgressPercent(elapsedSeconds) {
  const boundedSeconds = Math.max(
    0,
    Math.min(WALK_DURATION_SECONDS, Math.floor(elapsedSeconds)),
  );

  if (boundedSeconds <= WALK_TURN_AROUND_SECONDS) {
    return (boundedSeconds / WALK_TURN_AROUND_SECONDS) * 100;
  }

  return Math.max(
    0,
    100 -
      ((boundedSeconds - WALK_TURN_AROUND_SECONDS) / WALK_TURN_AROUND_SECONDS) *
        100,
  );
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

function haversineDistanceMeters(a, b) {
  const lat1 = Number(a?.lat);
  const lon1 = Number(a?.lng);
  const lat2 = Number(b?.lat);
  const lon2 = Number(b?.lng);

  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return 0;
  }

  const earthRadiusMeters = 6371e3;
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const lat1Radians = toRadians(lat1);
  const lat2Radians = toRadians(lat2);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1Radians) *
      Math.cos(lat2Radians) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function roundTo(value, digits = 1) {
  if (!Number.isFinite(value)) {
    return null;
  }

  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
}

function projectRelativeMeters(origin, point) {
  const originLat = Number(origin?.lat);
  const originLng = Number(origin?.lng);
  const pointLat = Number(point?.lat);
  const pointLng = Number(point?.lng);

  if (
    !Number.isFinite(originLat) ||
    !Number.isFinite(originLng) ||
    !Number.isFinite(pointLat) ||
    !Number.isFinite(pointLng)
  ) {
    return null;
  }

  const earthRadiusMeters = 6371e3;
  const originLatRadians = toRadians(originLat);
  const pointLatRadians = toRadians(pointLat);
  const avgLatRadians = (originLatRadians + pointLatRadians) / 2;
  const dLat = pointLatRadians - originLatRadians;
  const dLng = toRadians(pointLng - originLng);

  return {
    xMeters: earthRadiusMeters * dLng * Math.cos(avgLatRadians),
    yMeters: earthRadiusMeters * dLat,
  };
}

function buildRouteTracePoint(origin, point, totalDistanceMeters = 0) {
  const projected = projectRelativeMeters(origin, point);
  const pointTimestampMs = Number(point?.timestamp);
  const originTimestampMs = Number(origin?.timestamp);

  if (!projected) {
    return null;
  }

  return {
    xMeters: roundTo(projected.xMeters, 2),
    yMeters: roundTo(projected.yMeters, 2),
    tSeconds:
      Number.isFinite(pointTimestampMs) && Number.isFinite(originTimestampMs)
        ? roundTo(Math.max(0, (pointTimestampMs - originTimestampMs) / 1000), 1)
        : null,
    totalDistanceMeters: roundTo(totalDistanceMeters, 1),
  };
}

function buildGeoRouteTracePoint(origin, point, totalDistanceMeters = 0) {
  const lat = Number(point?.lat);
  const lng = Number(point?.lng);
  const pointTimestampMs = Number(point?.timestamp);
  const originTimestampMs = Number(origin?.timestamp);
  const accuracyMeters = readFiniteNumber(point?.accuracy);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return {
    lat: roundTo(lat, 6),
    lng: roundTo(lng, 6),
    tSeconds:
      Number.isFinite(pointTimestampMs) && Number.isFinite(originTimestampMs)
        ? roundTo(Math.max(0, (pointTimestampMs - originTimestampMs) / 1000), 1)
        : null,
    totalDistanceMeters: roundTo(totalDistanceMeters, 1),
    accuracyMeters:
      accuracyMeters != null ? Math.max(0, roundTo(accuracyMeters, 1)) : null,
  };
}

function buildGeoTrackingPoint(position) {
  const accuracy = Number(position?.coords?.accuracy);
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  const timestamp = Number(position?.timestamp || Date.now());

  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    (Number.isFinite(accuracy) && accuracy > MAX_ACCEPTED_ACCURACY_METERS)
  ) {
    return null;
  }

  return {
    lat: latitude,
    lng: longitude,
    timestamp,
    accuracy: Number.isFinite(accuracy) ? accuracy : null,
  };
}

function routeTraceDistanceMeters(a, b) {
  const x1 = Number(a?.xMeters);
  const y1 = Number(a?.yMeters);
  const x2 = Number(b?.xMeters);
  const y2 = Number(b?.yMeters);

  if (
    !Number.isFinite(x1) ||
    !Number.isFinite(y1) ||
    !Number.isFinite(x2) ||
    !Number.isFinite(y2)
  ) {
    return 0;
  }

  return Math.hypot(x2 - x1, y2 - y1);
}

function geoRouteTraceDistanceMeters(a, b) {
  return haversineDistanceMeters(a, b);
}

function appendRouteTracePoint(
  routeTraceRef,
  nextPoint,
  { force = false } = {},
) {
  if (!nextPoint) {
    return;
  }

  const routeTrace = Array.isArray(routeTraceRef.current)
    ? routeTraceRef.current
    : [];

  if (!routeTrace.length) {
    routeTraceRef.current = [nextPoint];
    return;
  }

  const previousPoint = routeTrace[routeTrace.length - 1];
  const secondsDelta =
    readFiniteNumber(nextPoint?.tSeconds) != null &&
    readFiniteNumber(previousPoint?.tSeconds) != null
      ? nextPoint.tSeconds - previousPoint.tSeconds
      : null;
  const distanceDelta = routeTraceDistanceMeters(previousPoint, nextPoint);

  if (
    !force &&
    secondsDelta != null &&
    secondsDelta < ROUTE_TRACE_MIN_POINT_INTERVAL_MS / 1000 &&
    distanceDelta < ROUTE_TRACE_MIN_POINT_DISTANCE_METERS
  ) {
    return;
  }

  if (
    !force &&
    distanceDelta < 0.05 &&
    (secondsDelta == null || secondsDelta < 0.1)
  ) {
    return;
  }

  routeTraceRef.current = [...routeTrace, nextPoint];
}

function appendGeoRouteTracePoint(
  geoRouteTraceRef,
  nextPoint,
  { force = false } = {},
) {
  if (!nextPoint) {
    return;
  }

  const geoRouteTrace = Array.isArray(geoRouteTraceRef.current)
    ? geoRouteTraceRef.current
    : [];

  if (!geoRouteTrace.length) {
    geoRouteTraceRef.current = [nextPoint];
    return;
  }

  const previousPoint = geoRouteTrace[geoRouteTrace.length - 1];
  const secondsDelta =
    readFiniteNumber(nextPoint?.tSeconds) != null &&
    readFiniteNumber(previousPoint?.tSeconds) != null
      ? nextPoint.tSeconds - previousPoint.tSeconds
      : null;
  const distanceDelta = geoRouteTraceDistanceMeters(previousPoint, nextPoint);

  if (
    !force &&
    secondsDelta != null &&
    secondsDelta < ROUTE_TRACE_MIN_POINT_INTERVAL_MS / 1000 &&
    distanceDelta < ROUTE_TRACE_MIN_POINT_DISTANCE_METERS
  ) {
    return;
  }

  if (
    !force &&
    distanceDelta < 0.05 &&
    (secondsDelta == null || secondsDelta < 0.1)
  ) {
    return;
  }

  geoRouteTraceRef.current = [...geoRouteTrace, nextPoint];
}

function compressRouteTrace(points = [], maxPoints = ROUTE_TRACE_MAX_POINTS) {
  if (!Array.isArray(points) || points.length <= maxPoints) {
    return Array.isArray(points) ? points : [];
  }

  const keepCount = Math.max(2, Math.floor(maxPoints));
  const lastIndex = points.length - 1;
  const compacted = [];

  for (let slot = 0; slot < keepCount; slot += 1) {
    const index =
      slot === keepCount - 1
        ? lastIndex
        : Math.round((slot * lastIndex) / (keepCount - 1));
    const point = points[index];

    if (point && compacted[compacted.length - 1] !== point) {
      compacted.push(point);
    }
  }

  return compacted;
}

function speakPrompt({ text, lang, audioMuted = false }) {
  if (
    audioMuted ||
    !text ||
    typeof window === "undefined" ||
    typeof window.SpeechSynthesisUtterance !== "function" ||
    !window.speechSynthesis
  ) {
    return;
  }

  try {
    const utterance = new window.SpeechSynthesisUtterance(text);
    utterance.lang = lang || "en-GB";
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
  } catch {}
}

function resolveResumePath(assessment, nowMs = Date.now()) {
  if (assessment?.postMeasurementId) {
    return `${BASE_PATH}/post`;
  }

  if (hasPostRecordingContinueIntent(assessment)) {
    return `${BASE_PATH}/post`;
  }

  if (hasActivePostRecordingResume(assessment, nowMs)) {
    return `${BASE_PATH}/post`;
  }

  if (assessment?.walk) {
    return `${BASE_PATH}/walk`;
  }

  if (assessment?.preMeasurementId) {
    return `${BASE_PATH}/walk`;
  }

  return `${BASE_PATH}/pre`;
}

function AssessmentCard({
  title,
  body,
  primaryAction = null,
  secondaryAction = null,
  children = null,
  footer = null,
  scrollable = true,
}) {
  const hasHeader = Boolean(title) || Boolean(body);

  return (
    <div
      className={[
        "w-full max-w-lg h-full min-h-0 px-4",
        scrollable ? "overflow-y-auto" : "overflow-hidden",
      ].join(" ")}
    >
      <section
        className={[
          "flex flex-col gap-4",
          scrollable ? "min-h-full" : "h-full min-h-0",
        ].join(" ")}
      >
        {hasHeader ? (
          <header className="flex flex-col gap-1 text-left">
            {title ? <h1 className="subtitle-text">{title}</h1> : null}
            {body ? <p>{body}</p> : null}
          </header>
        ) : null}

        {children}

        {footer ? (
          footer
        ) : primaryAction || secondaryAction ? (
          <div className="mt-auto grid grid-cols-1 gap-2 pb-2">
            {primaryAction ? (
              <button
                type="button"
                className={primaryAction.className || "primary-button w-full"}
                onClick={primaryAction.onClick}
              >
                {primaryAction.label}
              </button>
            ) : null}

            {secondaryAction ? (
              <button
                type="button"
                className={
                  secondaryAction.className || "secondary-button w-full"
                }
                onClick={secondaryAction.onClick}
              >
                {secondaryAction.label}
              </button>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function getSixMinuteWalkStepShape(index, total) {
  if (total <= 1) {
    return "rounded-2xl";
  }

  if (index === 0) {
    return "rounded-t-2xl rounded-b-sm";
  }

  if (index === total - 1) {
    return "rounded-t-sm rounded-b-2xl";
  }

  return "rounded-sm";
}

function SixMinuteWalkStepCard({ icon, title, body, shapeClassName }) {
  return (
    <li
      className={[
        "w-full bg-card px-3 py-3 shadow text-left",
        shapeClassName || "rounded-2xl",
      ].join(" ")}
      aria-label={typeof title === "string" ? title : undefined}
    >
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center text-primary">
          {icon}
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-sm font-semibold text-typography">{title}</p>
          <p className="muted-text text-sm leading-6">{body}</p>
        </div>
      </div>
    </li>
  );
}

function SixMinuteWalkIntroCard({
  assessment,
  onStart,
  onContinue,
  onRestart,
}) {
  const { t } = useTranslation();
  const stepCount = 3;

  const hasProgress =
    Boolean(assessment?.preMeasurementId) ||
    Boolean(assessment?.walk) ||
    Boolean(assessment?.postMeasurementId);

  return (
    <AssessmentCard
      title={t(
        "result.sixMinuteWalk.flowTitle",
        "6-Minute Walk Assessment",
      )}
      body={t(
        "result.sixMinuteWalk.flowIntro",
        "This assessment includes a baseline recording, a 6-minute walk test, and a final post-walk recording.",
      )}
      primaryAction={{
        label: hasProgress
          ? t("result.sixMinuteWalk.continueAssessment", "Continue assessment")
          : t("result.sixMinuteWalk.startAssessment", "Start assessment"),
        onClick: hasProgress ? onContinue : onStart,
      }}
      secondaryAction={
        hasProgress
          ? {
              className: "!w-full small-outlined-button",
              label: t("result.sixMinuteWalk.restartAssessment", "Restart"),
              onClick: onRestart,
            }
          : null
      }
    >
      <ol className="m-0 flex list-none flex-col gap-1 p-0 text-left">
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(0, stepCount)}
          icon={
            <VideocamOutlinedIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step1Title", "1. Baseline recording")}
          body={t(
            "result.sixMinuteWalk.step1Body",
            "Record a seated baseline measurement, follow the on-screen prompts, then return to normal and remain still.",
          )}
        />
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(1, stepCount)}
          icon={
            <DirectionsWalkIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step2Title", "2. Timed walk")}
          body={t(
            "result.sixMinuteWalk.step2Body",
            "Walk for 6 minutes with your phone. The app will prompt you to turn around after 3 minutes and will measure the distance travelled.",
          )}
        />
        <SixMinuteWalkStepCard
          shapeClassName={getSixMinuteWalkStepShape(2, stepCount)}
          icon={
            <VideocamOutlinedIcon
              aria-hidden="true"
              className="!text-[1.65rem]"
            />
          }
          title={t("result.sixMinuteWalk.step3Title", "3. Post-walk recording")}
          body={t(
            "result.sixMinuteWalk.step3Body",
            "Sit back down and repeat the recording as soon as you are ready, then return to normal and remain still.",
          )}
        />
      </ol>
    </AssessmentCard>
  );
}

function SixMinuteWalkWalkPage({
  assessment,
  setAssessment,
  audioMuted = false,
  resumePostRecording = false,
  onContinueToPost = null,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [distanceMeters, setDistanceMeters] = useState(0);
  const [sixMinuteDistanceMeters, setSixMinuteDistanceMeters] = useState(null);
  const [distanceFromStartMeters, setDistanceFromStartMeters] = useState(0);
  const [hasReturnedToStart, setHasReturnedToStart] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [routeTracePreview, setRouteTracePreview] = useState([]);
  const [geoRouteTracePreview, setGeoRouteTracePreview] = useState([]);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(null);
  const startPointRef = useRef(null);
  const lastPointRef = useRef(null);
  const distanceMetersRef = useRef(0);
  const distanceFromStartMetersRef = useRef(0);
  const sixMinuteDistanceMetersRef = useRef(null);
  const sampleCountRef = useRef(0);
  const midpointPromptedRef = useRef(false);
  const timedCompleteRef = useRef(false);
  const sixMinuteCompletedAtRef = useRef(null);
  const returnedAtRef = useRef(null);
  const wakeLockRef = useRef(null);
  const routeTraceRef = useRef([]);
  const geoRouteTraceRef = useRef([]);
  const rejectedLocationSampleCountRef = useRef(0);
  const discardedSpeedSampleCountRef = useRef(0);
  const lastLiveWalkDebugStateRef = useRef("");
  const resumeTimeToRecordingStartedAtMs = parseTimestampMs(
    getPostRecordingResumeStartAt(assessment),
  );
  const [resumeElapsedSeconds, setResumeElapsedSeconds] = useState(() => {
    if (
      !resumePostRecording ||
      !Number.isFinite(resumeTimeToRecordingStartedAtMs)
    ) {
      return 0;
    }

    return Math.max(
      0,
      Math.floor((Date.now() - resumeTimeToRecordingStartedAtMs) / 1000),
    );
  });

  const releaseWakeLock = useCallback(async () => {
    const wakeLock = wakeLockRef.current;
    wakeLockRef.current = null;

    if (!wakeLock || typeof wakeLock.release !== "function") {
      return;
    }

    try {
      await wakeLock.release();
    } catch {}
  }, []);

  const requestWakeLock = useCallback(async () => {
    if (
      typeof document === "undefined" ||
      document.visibilityState === "hidden" ||
      typeof navigator === "undefined" ||
      !navigator.wakeLock ||
      typeof navigator.wakeLock.request !== "function"
    ) {
      return;
    }

    if (wakeLockRef.current && !wakeLockRef.current.released) {
      return;
    }

    try {
      const wakeLock = await navigator.wakeLock.request("screen");
      wakeLockRef.current = wakeLock;
      wakeLock?.addEventListener?.("release", () => {
        if (wakeLockRef.current === wakeLock) {
          wakeLockRef.current = null;
        }
      });
    } catch {}
  }, []);

  const clearTracking = useCallback(() => {
    if (
      watchIdRef.current != null &&
      typeof navigator !== "undefined" &&
      navigator.geolocation
    ) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;

    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
    }
    intervalRef.current = null;
  }, []);

  const appendLiveRouteTracePoint = useCallback((point, options = {}) => {
    appendRouteTracePoint(routeTraceRef, point, options);
    setRouteTracePreview([...routeTraceRef.current]);
  }, []);
  const appendLiveGeoRouteTracePoint = useCallback((point, options = {}) => {
    appendGeoRouteTracePoint(geoRouteTraceRef, point, options);
    setGeoRouteTracePreview([...geoRouteTraceRef.current]);
  }, []);

  useEffect(() => {
    if (!resumePostRecording || !assessment?.walk) {
      return undefined;
    }

    const walkSummary = assessment.walk;
    const restoredElapsedMs =
      readFiniteNumber(walkSummary?.elapsedMs) ?? WALK_DURATION_SECONDS * 1000;
    const restoredDistanceMeters =
      readFiniteNumber(walkSummary?.totalDistanceMeters) ?? 0;
    const restoredTimedDistanceMeters =
      readFiniteNumber(walkSummary?.sixMinuteDistanceMeters) ??
      restoredDistanceMeters;
    const restoredDistanceFromStartMeters =
      readFiniteNumber(walkSummary?.distanceFromStartMeters) ?? 0;
    const restoredReturnedAt =
      assessment?.returnedAt || walkSummary?.returnedAt || null;

    setStatus("timed-complete");
    setElapsedSeconds(Math.max(0, Math.floor(restoredElapsedMs / 1000)));
    setDistanceMeters(restoredDistanceMeters);
    setSixMinuteDistanceMeters(restoredTimedDistanceMeters);
    setDistanceFromStartMeters(restoredDistanceFromStartMeters);
    setHasReturnedToStart(Boolean(restoredReturnedAt));
    setLocationError(null);

    distanceMetersRef.current = restoredDistanceMeters;
    sixMinuteDistanceMetersRef.current = restoredTimedDistanceMeters;
    distanceFromStartMetersRef.current = restoredDistanceFromStartMeters;
    sampleCountRef.current = readFiniteNumber(walkSummary?.sampleCount) ?? 0;
    timedCompleteRef.current = true;
    sixMinuteCompletedAtRef.current = walkSummary?.completedAt || null;
    returnedAtRef.current = restoredReturnedAt;
    routeTraceRef.current = Array.isArray(walkSummary?.routeTrace)
      ? walkSummary.routeTrace
      : [];
    geoRouteTraceRef.current = Array.isArray(walkSummary?.geoRouteTrace)
      ? walkSummary.geoRouteTrace
      : [];
    setRouteTracePreview([...routeTraceRef.current]);
    setGeoRouteTracePreview([...geoRouteTraceRef.current]);
    logSixMinuteWalkFlowDebug(
      "info",
      "Restored saved walk summary for resume.",
      {
        assessmentId: assessment?.assessmentId || null,
        routeTracePointCount: routeTraceRef.current.length,
        geoRoutePointCount: geoRouteTraceRef.current.length,
        totalDistanceMeters: restoredDistanceMeters,
        sixMinuteDistanceMeters: restoredTimedDistanceMeters,
        returnedAt: restoredReturnedAt,
      },
    );

    if (!Number.isFinite(resumeTimeToRecordingStartedAtMs)) {
      setResumeElapsedSeconds(0);
      return undefined;
    }

    const updateResumeElapsedSeconds = () => {
      setResumeElapsedSeconds(
        Math.max(
          0,
          Math.floor((Date.now() - resumeTimeToRecordingStartedAtMs) / 1000),
        ),
      );
    };

    updateResumeElapsedSeconds();
    const resumeIntervalId = window.setInterval(
      updateResumeElapsedSeconds,
      250,
    );

    return () => {
      window.clearInterval(resumeIntervalId);
    };
  }, [assessment, resumePostRecording, resumeTimeToRecordingStartedAtMs]);

  const finishTimedWalk = useCallback(() => {
    if (timedCompleteRef.current || !startTimeRef.current) {
      return;
    }

    timedCompleteRef.current = true;
    const completedAt = new Date(
      startTimeRef.current + WALK_DURATION_SECONDS * 1000,
    ).toISOString();
    const timedDistance = distanceMetersRef.current;

    sixMinuteCompletedAtRef.current = completedAt;
    sixMinuteDistanceMetersRef.current = timedDistance;
    setSixMinuteDistanceMeters(timedDistance);
    setStatus("timed-complete");

    if (
      !returnedAtRef.current &&
      distanceFromStartMetersRef.current <= RETURN_TO_START_THRESHOLD_METERS
    ) {
      returnedAtRef.current = completedAt;
      setHasReturnedToStart(true);
    }

    trackEvent("six_minute_walk_completed", {
      assessmentId: assessment?.assessmentId || null,
      distanceMeters: timedDistance,
      elapsedSeconds: WALK_DURATION_SECONDS,
    });
  }, [assessment?.assessmentId]);

  const proceedToPostRecording = useCallback(() => {
    if (resumePostRecording) {
      onContinueToPost?.();
      return;
    }

    if (!startTimeRef.current || !timedCompleteRef.current) {
      return;
    }

    if (startPointRef.current && lastPointRef.current) {
      appendLiveRouteTracePoint(
        buildRouteTracePoint(
          startPointRef.current,
          lastPointRef.current,
          distanceMetersRef.current,
        ),
        { force: true },
      );
      appendLiveGeoRouteTracePoint(
        buildGeoRouteTracePoint(
          startPointRef.current,
          lastPointRef.current,
          distanceMetersRef.current,
        ),
        { force: true },
      );
    }

    clearTracking();

    const startedAt = new Date(startTimeRef.current);
    const trackingStoppedAt = new Date();
    const timedCompletedAt =
      sixMinuteCompletedAtRef.current ||
      new Date(
        startTimeRef.current + WALK_DURATION_SECONDS * 1000,
      ).toISOString();
    const timedDistance =
      sixMinuteDistanceMetersRef.current ?? distanceMetersRef.current;
    const returnedAt =
      returnedAtRef.current ||
      (distanceFromStartMetersRef.current <= RETURN_TO_START_THRESHOLD_METERS
        ? trackingStoppedAt.toISOString()
        : null);
    const routeTrace = compressRouteTrace(routeTraceRef.current);
    const geoRouteTrace = compressRouteTrace(geoRouteTraceRef.current);
    const continuedAt = trackingStoppedAt.toISOString();
    const walkSummary = {
      startedAt: startedAt.toISOString(),
      completedAt: timedCompletedAt,
      elapsedMs: WALK_DURATION_SECONDS * 1000,
      sixMinuteDistanceMeters: timedDistance,
      totalDistanceMeters: distanceMetersRef.current,
      trackedElapsedMs: trackingStoppedAt.getTime() - startedAt.getTime(),
      trackingStoppedAt: trackingStoppedAt.toISOString(),
      distanceFromStartMeters: distanceFromStartMetersRef.current,
      sampleCount: sampleCountRef.current,
      turnAroundAtSeconds: WALK_TURN_AROUND_SECONDS,
      returnedAt,
      routeTrace,
      geoRouteTrace,
      routeTraceKind: routeTrace.length ? "relative-meters-v1" : null,
      geoRouteTraceKind: geoRouteTrace.length ? "wgs84-v1" : null,
    };
    logSixMinuteWalkFlowDebug("info", "Persisting completed walk summary.", {
      assessmentId: assessment?.assessmentId || null,
      routeTracePointCount: routeTrace.length,
      geoRoutePointCount: geoRouteTrace.length,
      totalDistanceMeters: distanceMetersRef.current,
      sixMinuteDistanceMeters: timedDistance,
      returnedAt,
      trackingStoppedAt: walkSummary.trackingStoppedAt,
    });
    const postResume = buildPostRecordingResumeState({
      completedAt: timedCompletedAt,
      trackingStoppedAt: trackingStoppedAt.toISOString(),
      returnedAt,
      continuedAt,
    });

    setAssessment((currentAssessment) => ({
      ...currentAssessment,
      walk: walkSummary,
      postResume,
      returnedAt,
    }));

    trackEvent("six_minute_walk_tracking_stopped", {
      assessmentId: assessment?.assessmentId || null,
      sixMinuteDistanceMeters: timedDistance,
      totalDistanceMeters: distanceMetersRef.current,
      returnedAt,
    });

    navigate(`${BASE_PATH}/post`, {
      replace: true,
      state: { autoStartPost: true },
    });
  }, [
    assessment?.assessmentId,
    appendLiveRouteTracePoint,
    appendLiveGeoRouteTracePoint,
    clearTracking,
    navigate,
    onContinueToPost,
    resumePostRecording,
    setAssessment,
  ]);

  const startWalk = useCallback(() => {
    logSixMinuteWalkFlowDebug("info", "Starting live geolocation tracking.", {
      assessmentId: assessment?.assessmentId || null,
      hasGeolocation: Boolean(
        typeof navigator !== "undefined" && navigator.geolocation,
      ),
      hasWatchPosition: Boolean(
        typeof navigator !== "undefined" &&
        navigator.geolocation &&
        typeof navigator.geolocation.watchPosition === "function",
      ),
    });

    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.watchPosition !== "function"
    ) {
      logSixMinuteWalkFlowDebug(
        "warn",
        "Browser geolocation is unavailable for the live walk.",
      );
      setLocationError("unsupported");
      setStatus("error");
      return;
    }

    setLocationError(null);
    setStatus("walking");
    setElapsedSeconds(0);
    setDistanceMeters(0);
    setSixMinuteDistanceMeters(null);
    setDistanceFromStartMeters(0);
    setHasReturnedToStart(false);
    startPointRef.current = null;
    lastPointRef.current = null;
    distanceMetersRef.current = 0;
    distanceFromStartMetersRef.current = 0;
    sixMinuteDistanceMetersRef.current = null;
    sampleCountRef.current = 0;
    midpointPromptedRef.current = false;
    timedCompleteRef.current = false;
    sixMinuteCompletedAtRef.current = null;
    returnedAtRef.current = null;
    routeTraceRef.current = [];
    geoRouteTraceRef.current = [];
    rejectedLocationSampleCountRef.current = 0;
    discardedSpeedSampleCountRef.current = 0;
    lastLiveWalkDebugStateRef.current = "";
    if (
      status !== "idle" ||
      routeTracePreview.length > 1 ||
      geoRouteTracePreview.length > 1
    ) {
      setRouteTracePreview([]);
      setGeoRouteTracePreview([]);
    }
    startTimeRef.current = Date.now();

    trackEvent("six_minute_walk_started", {
      assessmentId: assessment?.assessmentId || null,
    });

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const accuracy = Number(position?.coords?.accuracy);
        const nextPoint = buildGeoTrackingPoint(position);

        if (!nextPoint) {
          rejectedLocationSampleCountRef.current += 1;
          const rejectedSampleCount = rejectedLocationSampleCountRef.current;

          if (rejectedSampleCount <= 3 || rejectedSampleCount % 10 === 0) {
            logSixMinuteWalkFlowDebug(
              "warn",
              "Ignored live location sample because it was missing coordinates or too inaccurate.",
              {
                rejectedSampleCount,
                latitude: readFiniteNumber(position?.coords?.latitude),
                longitude: readFiniteNumber(position?.coords?.longitude),
                accuracyMeters: Number.isFinite(accuracy) ? accuracy : null,
                maxAcceptedAccuracyMeters: MAX_ACCEPTED_ACCURACY_METERS,
              },
            );
          }

          return;
        }

        sampleCountRef.current += 1;

        if (!lastPointRef.current || !startPointRef.current) {
          startPointRef.current = nextPoint;
          lastPointRef.current = nextPoint;
          appendLiveRouteTracePoint(
            buildRouteTracePoint(nextPoint, nextPoint, 0),
            { force: true },
          );
          appendLiveGeoRouteTracePoint(
            buildGeoRouteTracePoint(nextPoint, nextPoint, 0),
            { force: true },
          );
          setDistanceFromStartMeters(0);
          return;
        }

        const deltaSeconds =
          Math.max(1, nextPoint.timestamp - lastPointRef.current.timestamp) /
          1000;
        const segmentDistance = haversineDistanceMeters(
          lastPointRef.current,
          nextPoint,
        );
        const speed = segmentDistance / deltaSeconds;

        if (speed <= MAX_ACCEPTED_SPEED_METRES_PER_SECOND) {
          const nextDistanceMeters =
            distanceMetersRef.current + segmentDistance;
          distanceMetersRef.current = nextDistanceMeters;
          setDistanceMeters(nextDistanceMeters);
          lastPointRef.current = nextPoint;

          const nextDistanceFromStart = haversineDistanceMeters(
            startPointRef.current,
            nextPoint,
          );
          distanceFromStartMetersRef.current = nextDistanceFromStart;
          setDistanceFromStartMeters(nextDistanceFromStart);
          appendLiveRouteTracePoint(
            buildRouteTracePoint(
              startPointRef.current,
              nextPoint,
              nextDistanceMeters,
            ),
          );
          appendLiveGeoRouteTracePoint(
            buildGeoRouteTracePoint(
              startPointRef.current,
              nextPoint,
              nextDistanceMeters,
            ),
          );

          if (
            !returnedAtRef.current &&
            timedCompleteRef.current &&
            nextDistanceFromStart <= RETURN_TO_START_THRESHOLD_METERS
          ) {
            returnedAtRef.current = new Date(nextPoint.timestamp).toISOString();
            setHasReturnedToStart(true);
          }
        } else {
          discardedSpeedSampleCountRef.current += 1;
          const discardedSampleCount = discardedSpeedSampleCountRef.current;

          if (discardedSampleCount <= 3 || discardedSampleCount % 10 === 0) {
            logSixMinuteWalkFlowDebug(
              "warn",
              "Ignored live location sample because the inferred speed was too high.",
              {
                discardedSampleCount,
                segmentDistanceMeters: roundTo(segmentDistance, 1),
                deltaSeconds: roundTo(deltaSeconds, 2),
                speedMetresPerSecond: roundTo(speed, 2),
                maxAcceptedSpeedMetresPerSecond:
                  MAX_ACCEPTED_SPEED_METRES_PER_SECOND,
              },
            );
          }
        }
      },
      (error) => {
        logSixMinuteWalkFlowDebug("error", "Live geolocation watch failed.", {
          code: error?.code || null,
          message: error?.message || null,
          timedComplete: timedCompleteRef.current,
          routeTracePointCount: routeTraceRef.current.length,
          geoRoutePointCount: geoRouteTraceRef.current.length,
        });
        setLocationError(error?.code || "permission");
        clearTracking();
        setStatus(timedCompleteRef.current ? "timed-complete" : "error");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      },
    );

    intervalRef.current = window.setInterval(() => {
      const startedAt = startTimeRef.current;
      if (!startedAt) {
        return;
      }

      const nextElapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);

      setElapsedSeconds(nextElapsedSeconds);

      if (
        nextElapsedSeconds >= WALK_TURN_AROUND_SECONDS &&
        !midpointPromptedRef.current
      ) {
        midpointPromptedRef.current = true;
        speakPrompt({
          text: t(
            "result.sixMinuteWalk.turnAroundPrompt",
            "Three minutes completed. Please turn around and head back now.",
          ),
          lang: i18n.resolvedLanguage || i18n.language,
          audioMuted,
        });
      }

      if (nextElapsedSeconds >= WALK_DURATION_SECONDS) {
        finishTimedWalk();
      }
    }, 250);
  }, [
    assessment?.assessmentId,
    appendLiveRouteTracePoint,
    appendLiveGeoRouteTracePoint,
    audioMuted,
    clearTracking,
    finishTimedWalk,
    geoRouteTracePreview.length,
    i18n.language,
    i18n.resolvedLanguage,
    routeTracePreview.length,
    status,
    t,
  ]);

  useEffect(() => {
    const routeTracePointCount = routeTracePreview.length;
    const geoRoutePointCount = geoRouteTracePreview.length;
    const stateKey = [
      status,
      routeTracePointCount,
      geoRoutePointCount,
      locationError || "ok",
      hasReturnedToStart ? "returned" : "not-returned",
    ].join("|");

    if (lastLiveWalkDebugStateRef.current === stateKey) {
      return;
    }

    lastLiveWalkDebugStateRef.current = stateKey;

    const isRoutineProgressUpdate =
      status === "walking" &&
      !locationError &&
      geoRoutePointCount > 3 &&
      geoRoutePointCount % 10 !== 0;

    if (isRoutineProgressUpdate) {
      return;
    }

    logSixMinuteWalkFlowDebug("info", "Live walk state changed.", {
      status,
      routeTracePointCount,
      geoRoutePointCount,
      distanceMeters: roundTo(distanceMeters, 1),
      distanceFromStartMeters: roundTo(distanceFromStartMeters, 1),
      hasReturnedToStart,
      locationError,
      lastGeoPoint: geoRouteTracePreview[geoRoutePointCount - 1] || null,
      lastRoutePoint: routeTracePreview[routeTracePointCount - 1] || null,
    });
  }, [
    distanceFromStartMeters,
    distanceMeters,
    geoRouteTracePreview,
    hasReturnedToStart,
    locationError,
    routeTracePreview,
    status,
  ]);

  useEffect(() => clearTracking, [clearTracking]);

  const hasPreMeasurement = Boolean(assessment?.preMeasurementId);
  const timedElapsedSeconds = Math.min(elapsedSeconds, WALK_DURATION_SECONDS);
  const progressPercent = computeWalkProgressPercent(timedElapsedSeconds);
  const isWalking = status === "walking";
  const isTimedComplete = status === "timed-complete";
  const hasStarted = isWalking || isTimedComplete;
  const isReturning = timedElapsedSeconds >= WALK_TURN_AROUND_SECONDS;
  const highlightedDistanceMeters = sixMinuteDistanceMeters ?? distanceMeters;
  const timerLabel = resumePostRecording
    ? t("result.sixMinuteWalk.summaryTimeToRecordingLabel", "Time to recording")
    : t("result.sixMinuteWalk.timerLabel", "Timer");
  const timerValue = resumePostRecording
    ? formatDurationLabel(resumeElapsedSeconds)
    : formatDurationLabel(
        Math.max(0, WALK_DURATION_SECONDS - timedElapsedSeconds),
      );
  const walkBody = isTimedComplete
    ? t(
        "result.sixMinuteWalk.walkCompleteBody",
        "Walk complete. Set yourself up as before, then continue to the post-walk recording.",
      )
    : isReturning
      ? t(
          "result.sixMinuteWalk.walkReturnBody",
          "Walk back to your starting point and set up for your next recording.",
        )
      : t(
          "result.sixMinuteWalk.walkBody",
          "Walk away from your starting point and turn around at 3 minutes.",
        );
  const walkStageTitle = isTimedComplete
    ? t("result.sixMinuteWalk.stageCompleteTitle", "Proceed to next step")
    : hasStarted
      ? isReturning
        ? t("result.sixMinuteWalk.stageReturnTitle", "Walk back")
        : t("result.sixMinuteWalk.stageOutTitle", "Walk out")
      : t("result.sixMinuteWalk.stageStartTitle", "Start walk");
  const walkStageBody = walkBody;

  useEffect(() => {
    if (
      !hasPreMeasurement ||
      hasStarted ||
      resumePostRecording ||
      !GOOGLE_MAPS_ENABLED
    ) {
      return undefined;
    }

    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      logSixMinuteWalkFlowDebug(
        "warn",
        "Skipping pre-start location preview because browser geolocation is unavailable.",
      );
      return undefined;
    }

    let cancelled = false;
    logSixMinuteWalkFlowDebug(
      "info",
      "Requesting pre-start location preview.",
      {
        assessmentId: assessment?.assessmentId || null,
      },
    );

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (cancelled) {
          return;
        }

        const nextPoint = buildGeoTrackingPoint(position);

        if (!nextPoint) {
          logSixMinuteWalkFlowDebug(
            "warn",
            "Ignored pre-start location preview because it was missing coordinates or too inaccurate.",
            {
              accuracyMeters: readFiniteNumber(position?.coords?.accuracy),
              maxAcceptedAccuracyMeters: MAX_ACCEPTED_ACCURACY_METERS,
            },
          );
          return;
        }

        const routePreviewPoint = buildRouteTracePoint(nextPoint, nextPoint, 0);
        const geoPreviewPoint = buildGeoRouteTracePoint(
          nextPoint,
          nextPoint,
          0,
        );

        setLocationError(null);
        setRouteTracePreview(routePreviewPoint ? [routePreviewPoint] : []);
        setGeoRouteTracePreview(geoPreviewPoint ? [geoPreviewPoint] : []);

        logSixMinuteWalkFlowDebug(
          "info",
          "Loaded pre-start location preview.",
          {
            point: geoPreviewPoint,
          },
        );
      },
      (error) => {
        if (cancelled) {
          return;
        }

        logSixMinuteWalkFlowDebug(
          "warn",
          "Pre-start location preview failed.",
          {
            code: error?.code || null,
            message: error?.message || null,
          },
        );
        setLocationError(error?.code || "permission");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15000,
      },
    );

    return () => {
      cancelled = true;
    };
  }, [
    assessment?.assessmentId,
    hasPreMeasurement,
    hasStarted,
    resumePostRecording,
  ]);

  useEffect(() => {
    if (!hasPreMeasurement || !hasStarted) {
      releaseWakeLock();
      return undefined;
    }

    requestWakeLock();

    if (typeof document === "undefined") {
      return () => {
        releaseWakeLock();
      };
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
      } else {
        releaseWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      releaseWakeLock();
    };
  }, [hasPreMeasurement, hasStarted, releaseWakeLock, requestWakeLock]);

  if (!hasPreMeasurement) {
    return <Navigate to={BASE_PATH} replace />;
  }

  const footer = (
    <div className="mt-auto flex flex-col gap-3 pb-2">
      {!hasStarted || status === "error" ? (
        <button
          type="button"
          className="primary-button w-full"
          onClick={startWalk}
        >
          {t("result.sixMinuteWalk.startWalk", "Start walk")}
        </button>
      ) : null}

      {isTimedComplete ? (
        <button
          type="button"
          className="primary-button w-full"
          onClick={proceedToPostRecording}
        >
          {t("result.sixMinuteWalk.continueButton", "Continue")}
        </button>
      ) : null}

      {hasStarted ? (
        <div className="flex justify-end">
          <button
            type="button"
            className="small-outlined-button"
            onClick={() => navigate(BASE_PATH, { replace: true })}
          >
            {t("result.sixMinuteWalk.cancelAssessment", "Cancel")}
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <AssessmentCard title={null} body={null} footer={footer} scrollable={false}>
      <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-card p-4 shadow">
        <div className="flex min-h-0 flex-1 items-stretch overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1">
            <h1 className="subtitle-text">{walkStageTitle}</h1>
            <p>{walkStageBody}</p>
            <SixMinuteWalkRouteMap
              geoPoints={geoRouteTracePreview}
              fallbackPoints={routeTracePreview}
              ariaLabel={t(
                "result.sixMinuteWalk.liveRouteAriaLabel",
                "Live walk route preview",
              )}
              height="100%"
              className="min-h-[16rem] flex-1"
              fillContainer
              showEmptyState
              showCurrentLocationIcon
            />
          </div>
        </div>
      </section>

      <div className="card flex flex-col gap-4 p-4 text-left">
        <div className="flex items-end gap-3">
          <span
            aria-hidden="true"
            className="material-symbols-outlined mb-0.5 shrink-0 text-[1.9rem] leading-none text-secondary"
          >
            home_pin
          </span>

          <div className="relative flex-1 pt-8">
            <DirectionsWalkIcon
              className="absolute top-0 !h-7 !w-7 text-primary transition-[left,transform] duration-200"
              style={{
                left: `${progressPercent}%`,
                transform: `translateX(-50%)${isReturning ? " scaleX(-1)" : ""}`,
              }}
            />

            <div className="h-3.5 w-full overflow-hidden rounded-full bg-greyscale-1">
              <div
                className="h-full bg-primary transition-[width] duration-200"
                style={{
                  width: `${progressPercent}%`,
                }}
              />
            </div>
          </div>

          <span
            aria-hidden="true"
            className="material-symbols-outlined mb-0.5 shrink-0 text-[1.9rem] leading-none text-secondary"
          >
            flag_2
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="muted-text text-xs uppercase tracking-wide">
              {timerLabel}
            </p>
            <p className="text-3xl font-semibold text-primary">{timerValue}</p>
          </div>
          <div>
            <p className="muted-text text-xs uppercase tracking-wide">
              {t(
                isTimedComplete
                  ? "result.sixMinuteWalk.sixMinuteDistanceLabel"
                  : "result.sixMinuteWalk.distanceLabel",
                isTimedComplete ? "6-minute distance" : "Distance",
              )}
            </p>
            <p className="text-3xl font-semibold text-primary">
              {formatDistanceLabel(highlightedDistanceMeters)}{" "}
              <span className="text-base font-medium text-greyscale-3">m</span>
            </p>
          </div>
        </div>

        {locationError ? (
          <p className="text-sm text-red-700">
            {locationError === "unsupported"
              ? t(
                  "result.sixMinuteWalk.locationUnsupported",
                  "Location services are not available on this device.",
                )
              : t(
                  "result.sixMinuteWalk.locationError",
                  "We couldn't access your location. Please allow location access and try again.",
                )}
          </p>
        ) : null}
      </div>
    </AssessmentCard>
  );
}

export default memo(function SixMinuteWalkFlowPage({
  auth,
  storage,
  db,
  user,
  firstRun = false,
  onUploadStatus,
  onUploadProgress,
  onComplete,
  createMeasurementUploadSession,
  completeMeasurementUploadSession,
  failMeasurementUploadSession,
  analytics,
  subjectId = null,
  audioMuted = false,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [assessment, setAssessment] = useState(
    () => readStoredAssessment(user?.uid) || createAssessmentState(),
  );

  useEffect(() => {
    setAssessment(readStoredAssessment(user?.uid) || createAssessmentState());
  }, [user?.uid]);

  useEffect(() => {
    writeStoredAssessment(user?.uid, assessment);
  }, [assessment, user?.uid]);

  const replaceAssessment = useCallback(
    (nextAssessment) => {
      setAssessment(nextAssessment);
      writeStoredAssessment(user?.uid, nextAssessment);
    },
    [user?.uid],
  );
  const updateAssessment = useCallback(
    (updater) => {
      setAssessment((currentAssessment) => {
        const nextAssessment =
          typeof updater === "function" ? updater(currentAssessment) : updater;
        writeStoredAssessment(user?.uid, nextAssessment);
        return nextAssessment;
      });
    },
    [user?.uid],
  );

  const restartAssessment = useCallback(() => {
    const nextAssessment = createAssessmentState();
    replaceAssessment(nextAssessment);
    navigate(BASE_PATH, { replace: true });
  }, [navigate, replaceAssessment]);

  const startAssessment = useCallback(() => {
    const nextAssessment = createAssessmentState();
    replaceAssessment(nextAssessment);
    trackEvent("six_minute_walk_opened", {
      assessmentId: nextAssessment.assessmentId,
    });
    navigate(`${BASE_PATH}/pre`, { replace: true });
  }, [navigate, replaceAssessment]);

  const continueAssessment = useCallback(() => {
    navigate(resolveResumePath(assessment), { replace: true });
  }, [assessment, navigate]);

  const handlePreUploadStatus = useCallback(
    (event) => {
      const statusValue = getStatusValue(event);
      const uploadId = getUploadId(event);
      const variant = event?.variant || "RecordingSixMinuteWalk";
      const normalizedEvent = {
        ...(event && typeof event === "object" ? event : {}),
        uploadId,
        variant,
        // The six-minute-walk assessment is only complete after the post-walk
        // recording, so the baseline upload should still raise toasts but must
        // not tick the To Do task off early.
        suppressTaskCompletion: true,
      };

      onUploadStatus?.(normalizedEvent);

      if (statusValue !== "uploading" || !uploadId) {
        return;
      }

      updateAssessment((currentAssessment) => ({
        ...currentAssessment,
        preMeasurementId: uploadId,
      }));
      navigate(`${BASE_PATH}/walk`, { replace: true });
    },
    [navigate, onUploadStatus, updateAssessment],
  );

  const handlePostRecordingStarted = useCallback(
    ({ startedAt }) => {
      updateAssessment((currentAssessment) => {
        const returnedAtMs = currentAssessment?.returnedAt
          ? Date.parse(currentAssessment.returnedAt)
          : null;
        const startedAtMs = startedAt ? Date.parse(startedAt) : null;

        return {
          ...currentAssessment,
          postResume: null,
          postRecordingStartedAt: startedAt || null,
          recoveryDelayMs:
            Number.isFinite(returnedAtMs) && Number.isFinite(startedAtMs)
              ? Math.max(0, startedAtMs - returnedAtMs)
              : null,
        };
      });
    },
    [updateAssessment],
  );

  const handlePostComplete = useCallback(
    (event) => {
      const statusValue = getStatusValue(event);
      const uploadId = getUploadId(event);
      const variant = event?.variant || "RecordingSixMinuteWalk";
      const normalizedEvent = {
        ...(event && typeof event === "object" ? event : {}),
        uploadId,
        variant,
      };

      onUploadStatus?.(normalizedEvent);

      if (statusValue !== "uploading" || !uploadId) {
        return;
      }

      writeStoredAssessment(user?.uid, null);
      setAssessment(createAssessmentState());
      navigate(
        `/result?variant=${encodeURIComponent(
          variant,
        )}&docid=${encodeURIComponent(uploadId)}&context=${encodeURIComponent(
          RECORDING_MODES.SIX_MINUTE_WALK,
        )}&tab=${encodeURIComponent(RECORDING_MODES.SIX_MINUTE_WALK)}`,
        { replace: true },
      );
    },
    [navigate, onUploadStatus, user?.uid],
  );

  const preWorkflow = useMemo(
    () => ({
      assessmentType: RECORDING_MODES.SIX_MINUTE_WALK,
      assessmentId: assessment.assessmentId,
      stage: "pre",
      stageLabel: "baseline",
    }),
    [assessment.assessmentId],
  );

  const postWorkflow = useMemo(
    () => ({
      assessmentType: RECORDING_MODES.SIX_MINUTE_WALK,
      assessmentId: assessment.assessmentId,
      stage: "post",
      stageLabel: "recovery",
      pairedMeasurementId: assessment.preMeasurementId,
      preMeasurementId: assessment.preMeasurementId,
      walk: assessment.walk,
      returnedAt: assessment.returnedAt,
      postRecordingStartedAt: assessment.postRecordingStartedAt,
      recoveryDelayMs: assessment.recoveryDelayMs,
    }),
    [
      assessment.assessmentId,
      assessment.postRecordingStartedAt,
      assessment.preMeasurementId,
      assessment.recoveryDelayMs,
      assessment.returnedAt,
      assessment.walk,
    ],
  );

  const sharedPodProps = {
    auth,
    storage,
    db,
    user,
    onUploadProgress,
    onComplete,
    createMeasurementUploadSession,
    completeMeasurementUploadSession,
    failMeasurementUploadSession,
    analytics,
    subjectId,
    showLiveChart: false,
    enablePixelSignal: false,
    enableGrid3x3Pixel: false,
    audioMuted,
    appMode: RECORDING_MODES.SIX_MINUTE_WALK,
    variant: "RecordingSixMinuteWalk",
    navigateToResultOnUploadStart: false,
  };
  const shouldAutoStartPostRecording =
    location.state?.autoStartPost === true ||
    hasPostRecordingContinueIntent(assessment);
  const shouldShowPostRecordingResume =
    !shouldAutoStartPostRecording && hasActivePostRecordingResume(assessment);
  const continueToPostRecording = useCallback(() => {
    updateAssessment((currentAssessment) => ({
      ...currentAssessment,
      postResume: {
        ...(currentAssessment?.postResume || {}),
        continuedAt: new Date().toISOString(),
      },
    }));
    navigate(`${BASE_PATH}/post`, {
      replace: true,
      state: { autoStartPost: true },
    });
  }, [navigate, updateAssessment]);

  return (
    <Routes>
      <Route
        index
        element={
          <SixMinuteWalkIntroCard
            assessment={assessment}
            onStart={startAssessment}
            onContinue={continueAssessment}
            onRestart={restartAssessment}
          />
        }
      />

      <Route
        path="pre/*"
        element={
          <RemotePodLoader
            load={loadRecordingTidalBreathing}
            componentProps={{
              ...sharedPodProps,
              firstRun,
              recordingDurationSeconds: PRE_WALK_RECORDING_DURATION_SECONDS,
              phaseSchedule: PRE_WALK_PHASE_SCHEDULE,
              workflow: preWorkflow,
              onUploadStatus: handlePreUploadStatus,
            }}
          />
        }
      />

      <Route
        path="walk"
        element={
          <SixMinuteWalkWalkPage
            assessment={assessment}
            setAssessment={updateAssessment}
            audioMuted={audioMuted}
          />
        }
      />

      <Route
        path="post/*"
        element={
          assessment?.walk && assessment?.preMeasurementId ? (
            shouldShowPostRecordingResume ? (
              <SixMinuteWalkWalkPage
                assessment={assessment}
                setAssessment={updateAssessment}
                audioMuted={audioMuted}
                resumePostRecording
                onContinueToPost={continueToPostRecording}
              />
            ) : shouldAutoStartPostRecording ? (
              <RemotePodLoader
                load={loadRecordingTidalBreathing}
                componentProps={{
                  ...sharedPodProps,
                  firstRun: false,
                  recordingDurationSeconds:
                    POST_WALK_RECORDING_DURATION_SECONDS,
                  phaseSchedule: POST_WALK_PHASE_SCHEDULE,
                  workflow: postWorkflow,
                  onRecordingStarted: handlePostRecordingStarted,
                  onUploadStatus: handlePostComplete,
                }}
              />
            ) : (
              <Navigate to={`${BASE_PATH}/walk`} replace />
            )
          ) : (
            <Navigate to={BASE_PATH} replace />
          )
        }
      />

      <Route path="*" element={<Navigate to={BASE_PATH} replace />} />
    </Routes>
  );
});
